/**
 * useBilling Hooks
 * React Query hooks for billing and subscription state management
 */

import { useEffect, useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBundles,
  getPlanCopy,
  subscribe,
  confirmSubscription,
  getSubscriptionStatus,
  createPortalSession,
  cancelSubscription,
  reactivateSubscription,
  validateCoupon,
  getFeatures,
  getInvoices,
  getUsage,
  getAgentAddonQuote,
  enableAgentAddon,
  disableAgentAddon,
  undoDisableAgentAddon,
  createPaymentMethodSetupIntent,
  confirmPaymentMethod,
  type SubscriptionStatus,
} from '../api/billing';

// =============================================================================
// QUERY KEYS
// =============================================================================

const BILLING_QUERY_KEY = ['billing'];
const SUBSCRIPTION_KEY = [...BILLING_QUERY_KEY, 'subscription'];
const BUNDLES_KEY = [...BILLING_QUERY_KEY, 'bundles'];
const PLAN_COPY_KEY = [...BILLING_QUERY_KEY, 'plan-copy'];
const FEATURES_KEY = [...BILLING_QUERY_KEY, 'features'];
const INVOICES_KEY = [...BILLING_QUERY_KEY, 'invoices'];
const USAGE_KEY = [...BILLING_QUERY_KEY, 'usage'];
const AGENT_ADDON_QUOTE_KEY = [...BILLING_QUERY_KEY, 'agent-addon-quote'];

// =============================================================================
// QUERY HOOKS
// =============================================================================

/** How often to re-read the subscription while a caller awaits an outcome that has not shown up yet. */
const ADDON_CONVERGE_POLL_MS = 1500;
/** How long a caller awaits an outcome (add-on change, activation) before it stops polling. */
const AWAIT_OUTCOME_TIMEOUT_MS = 45000;
/** A subscription read this recent is served from cache on mount / navigation (no request). */
const SUBSCRIPTION_STALE_MS = 60_000;

/**
 * An outcome a caller is waiting on: `reached` says whether a read reports it. A wait's identity is the
 * object itself, so the caller keeps one stable object per wait (state, or a module constant) and creates
 * a new object to start a new wait, even for the same outcome.
 */
export type SubscriptionWait = { reached: (s: SubscriptionStatus) => boolean };

/** The subscription entitles a paid plan: active, and neither a trial nor still awaiting activation. */
export const AWAIT_ACTIVATION: SubscriptionWait = {
  reached: (s) => s.hasActiveSubscription && s.status !== 'trial' && s.status !== 'pending_activation',
};

/**
 * The subscription record a read describes: status, bundle, price and billing dates. The account's card and unit
 * count are left out: the confirm saves the card whatever its first charge does (BR-24 step 6), and units change alone.
 */
function sameSubscriptionRecord(a: SubscriptionStatus, b: SubscriptionStatus): boolean {
  return (
    a.status === b.status &&
    a.bundleName === b.bundleName &&
    a.unitPriceCents === b.unitPriceCents &&
    a.cancelAtPeriodEnd === b.cancelAtPeriodEnd &&
    a.currentPeriodEnd === b.currentPeriodEnd &&
    a.nextBillingDate === b.nextBillingDate
  );
}

/**
 * The outcome of a card confirm: an activated plan that is not the subscription the confirm started from. Until the
 * new plan activates, the read still serves that subscription, and a failed first charge restores it exactly (BR-86),
 * so neither reads as activated. Without a `before` read only AWAIT_ACTIVATION applies.
 */
export function awaitActivation(before: SubscriptionStatus | undefined): SubscriptionWait {
  return {
    reached: (s) => AWAIT_ACTIVATION.reached(s) && (before === undefined || !sameSubscriptionRecord(s, before)),
  };
}

/** A new wait for the agent add-on to report `status` (after an accepted add-on change). */
export function awaitAddonStatus(status: 'active' | 'cancelling'): SubscriptionWait {
  return { reached: (s) => s.agentAddon.status === status };
}

/**
 * Get current subscription status.
 * No background timer: the read refreshes on mount once stale (60s), and every billing mutation
 * invalidates it. A caller awaiting an outcome (`awaiting`) polls, fast, until the read reports it.
 * The hook owns that bound too: AWAIT_OUTCOME_TIMEOUT_MS after the wait began, `timedOut` is set and
 * polling stops. A timed-out wait stays timed out; a new wait object starts afresh.
 */
export function useBillingSubscription(awaiting: SubscriptionWait | null = null) {
  const [timedOutWait, setTimedOutWait] = useState<SubscriptionWait | null>(null);
  const timedOut = awaiting !== null && timedOutWait === awaiting;
  const read = useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: getSubscriptionStatus,
    staleTime: SUBSCRIPTION_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) =>
      awaiting !== null && !timedOut && !(query.state.data && awaiting.reached(query.state.data))
        ? ADDON_CONVERGE_POLL_MS
        : false,
  });
  const outcomeReached = awaiting !== null && read.data !== undefined && awaiting.reached(read.data);

  useEffect(() => {
    if (awaiting === null || outcomeReached) return undefined;
    const timer = setTimeout(() => setTimedOutWait(awaiting), AWAIT_OUTCOME_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [awaiting, outcomeReached]);

  // Attach the two fields to the tracked result rather than spreading it: a spread reads every prop,
  // which subscribes the caller to all of them (each fetch toggle would re-render it).
  return Object.assign(read, { outcomeReached, timedOut });
}

/**
 * Get available subscription bundles
 */
export function useBillingBundles() {
  return useQuery({
    queryKey: BUNDLES_KEY,
    queryFn: getBundles,
  });
}

/** How long the shared plan wording is served from cache: it is code-defined and changes only on deploy. */
const PLAN_COPY_STALE_MS = 10 * 60_000;

/** The shared "what's included" wording (GET /api/public/plan-copy). */
export function usePlanCopyQuery() {
  return useQuery({
    queryKey: PLAN_COPY_KEY,
    queryFn: getPlanCopy,
    staleTime: PLAN_COPY_STALE_MS,
  });
}

/**
 * Get subscription features
 */
export function useBillingFeatures() {
  return useQuery({
    queryKey: FEATURES_KEY,
    queryFn: getFeatures,
  });
}

/**
 * Get invoice history, newest first, one server page at a time. `data.invoices` is every page loaded
 * so far; call `fetchNextPage` while `hasNextPage` to reach older invoices.
 */
export function useBillingInvoices(enabled = true) {
  return useInfiniteQuery({
    queryKey: INVOICES_KEY,
    queryFn: ({ pageParam }) => getInvoices(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    select: (data) => ({ invoices: data.pages.flatMap((p) => p.invoices) }),
    enabled,
  });
}

/**
 * Get usage information
 */
export function useBillingUsage() {
  return useQuery({
    queryKey: USAGE_KEY,
    queryFn: getUsage,
  });
}

/**
 * Agent support add-on quote (prorated amount + dates). Only fetched while the turn-on
 * dialog is open; never cached so the amount is always the server's current proration — except that an
 * unresolved attempt under the persisted idempotency key returns its PINNED quote (BR-102).
 */
export function useAgentAddonQuote(enabled: boolean, getIdempotencyKey?: () => string | undefined) {
  return useQuery({
    queryKey: AGENT_ADDON_QUOTE_KEY,
    queryFn: () => getAgentAddonQuote(getIdempotencyKey?.()),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Subscribe to a bundle
 */
export function useSubscribeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: subscribe,
    onSuccess: () => {
      // Invalidate all billing queries to refresh data
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

/**
 * Confirm subscription (Phase 2) — server-side gate + emit CREATE_SUBSCRIPTION command. Resolves with the
 * subscription read the confirm started from (`before`), which the success page judges the outcome against.
 */
export function useConfirmSubscriptionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: Parameters<typeof confirmSubscription>[0]) => {
      // Read before the request: the invalidation after it refetches the cache, and the plan may activate meanwhile.
      const before = queryClient.getQueryData<SubscriptionStatus>(SUBSCRIPTION_KEY);
      return { ...(await confirmSubscription(params)), before };
    },
    onSuccess: () => {
      // Invalidate billing queries so the subscription page refreshes once the
      // consumer processes the command and the record appears in DynamoDB.
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

/**
 * Cancel subscription
 */
export function useCancelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY });
    },
  });
}

/**
 * Reactivate subscription
 */
export function useReactivateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reactivateSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY });
    },
  });
}

/**
 * Validate coupon code
 */
export function useRedeemCouponMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: validateCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY });
    },
  });
}

/**
 * Create Stripe billing portal session
 */
export function usePortalSessionMutation() {
  return useMutation({
    mutationFn: createPortalSession,
  });
}

/** Every agent-add-on / payment-method mutation refreshes the whole billing prefix. */
function useBillingPrefixMutation<TVars, TData>(mutationFn: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

/** Turn on agent support (charges the prorated amount to the default card). */
export function useEnableAgentAddonMutation() {
  return useBillingPrefixMutation(enableAgentAddon);
}

/** Turn off agent support (ends at the end of the billing period). */
export function useDisableAgentAddonMutation() {
  return useBillingPrefixMutation(disableAgentAddon);
}

/** Keep agent support while it is ending. */
export function useUndoDisableAgentAddonMutation() {
  return useBillingPrefixMutation(undoDisableAgentAddon);
}

/** SetupIntent for saving a card (no cache effect until confirmed). */
export function useCreatePaymentMethodSetupIntentMutation() {
  return useMutation({ mutationFn: createPaymentMethodSetupIntent });
}

/** Confirm a saved card; the subscription status carries the new default card. */
export function useConfirmPaymentMethodMutation() {
  return useBillingPrefixMutation(confirmPaymentMethod);
}
